import { useEffect, useMemo, useRef, useState } from 'react';
import ManorShell from './ManorShell';
import usePortrait from './usePortrait';
import { PopupMessage, RulesButton, RulesModal, useStageSubmit } from './StageChrome';
import scene from './scenes/stage2Scene';
import mobileScene from './scenes/stage2SceneMobile';

const TOTAL = 7;
const RULES = [
  'Observe the footsteps of ghostly apparitions by clicking on the play icon.',
  'Click on the footsteps of ghostly apparitions shown in the video.',
];
const TAP_RADIUS = 28; // px — footprints are small on a phone, so a near-miss counts

// Stage 2 (stage2.php): watch the footsteps clip, then click the 7 real footprints in
// the scene. Each click reveals the highlighted print; the 7th opens the message and
// closing it submits the stage (7 points, one per footprint).
export default function ManorStage2() {
  const submit = useStageSubmit(2);
  const portrait = usePortrait();
  const sceneRef = useRef(null);
  const found = useRef(new Set());
  const [showClip, setShowClip] = useState(true);
  const [showRules, setShowRules] = useState(false);
  const [done, setDone] = useState(false);
  const html = useMemo(() => (portrait ? mobileScene : scene), [portrait]); // portrait: phone artwork (Main .svg)

  // Rotating the phone swaps the scene markup: re-apply the prints already found.
  useEffect(() => {
    found.current.forEach((pos) => {
      const og = sceneRef.current?.querySelector(`#og${pos}`);
      const shown = sceneRef.current?.querySelector(`#img${pos}`);
      if (og) og.style.display = 'none';
      if (shown) shown.style.display = 'block';
    });
  }, [html]);

  const reveal = (hit) => {
    const pos = hit.getAttribute('pos');
    if (found.current.has(pos)) return;
    found.current.add(pos);
    hit.style.display = 'none';
    const shown = sceneRef.current.querySelector(`#img${pos}`);
    if (shown) shown.style.display = 'block';
    if (found.current.size === TOTAL) setDone(true);
  };

  const onSceneClick = (e) => {
    const hit = e.target.closest?.('.clickobject');
    if (hit) return reveal(hit);
    if (!portrait) return undefined;
    // Finger tap that missed the print: take the nearest one still hidden.
    let best = null;
    let bestD = TAP_RADIUS;
    sceneRef.current.querySelectorAll('.clickobject').forEach((el) => {
      if (found.current.has(el.getAttribute('pos'))) return;
      const r = el.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      if (d < bestD) {
        best = el;
        bestD = d;
      }
    });
    return best ? reveal(best) : undefined;
  };

  return (
    <ManorShell bgMob="img/stage2/popup_mob.jpg" stage={2} className="manor-stage2">
      <div
        key={portrait ? 'p' : 'l'}
        ref={sceneRef}
        className="manor-svgfile"
        onClick={onSceneClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <div className="manor-toolbar">
        <button type="button" className="manor-img-btn manor-play-btn" onClick={() => setShowClip(true)} aria-label="Play">
          <img src="/manor/img/Play.png" alt="Play" />
        </button>
        <RulesButton onClick={() => setShowRules(true)} />
      </div>

      {showClip && (
        <div className="manor-modal-overlay">
          <div className="manor-clip">
            <img src="/manor/img/stage2/popup.gif" alt="Footsteps" />
            <button type="button" className="manor-close-btn" onClick={() => setShowClip(false)}>OK</button>
          </div>
        </div>
      )}
      {showRules && <RulesModal rules={RULES} onClose={() => setShowRules(false)} />}
      {done && <PopupMessage src="/manor/img/stage2/Stage2_Popupmessage.png" alt="Stage 2 message" width="min(550px, 80vw)" onClose={() => submit(TOTAL)} />}
    </ManorShell>
  );
}
